import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import MessageBubble from './MessageBubble';
import JumpToLatestPill from './JumpToLatestPill';
import {
  buildBulkFollowUpByMessageId,
  buildAssistantFollowUpByMessageId,
  buildSuppressOutcomeAfterConfirmByMessageId,
  buildSuppressProgressAfterConfirmByMessageId
} from './messageListDerived';
import { toIsoTimestamp, toMillis } from './time';
import './MessageList.new.css';

const HIDDEN_CONFIRM_OUTCOME_WIDGET_TYPES = new Set(['patient_result', 'order_result']);
const SUPPRESSIBLE_CONFIRM_OUTCOME_WIDGET_TYPES = new Set([
  'status',
  'order_bulk_result',
  'patient_result',
  'order_result',
  'order_list',
  'patient_duplicate',
  'order_duplicate'
]);

// Slack-parity slack: treat the user as "at the bottom" within 80px so smooth
// auto-scroll engages even when subpixel layout, alignToBottom, or virtual
// rounding leaves a small gap. (KAP-312)
const AT_BOTTOM_THRESHOLD_PX = 80;

/**
 * Format a date for date dividers ("Today", "Yesterday", "March 17").
 */
function formatDateDivider(dateStr) {
  const dateMillis = toMillis(dateStr);
  if (!dateMillis) return '';
  const date = new Date(dateMillis);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (msgDate.getTime() === today.getTime()) return 'Today';
  if (msgDate.getTime() === yesterday.getTime()) return 'Yesterday';
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

/**
 * Get the date key (YYYY-MM-DD) for a message timestamp.
 */
function dateKey(timestamp) {
  const millis = toMillis(timestamp);
  if (!millis) return '';
  return new Date(millis).toISOString().slice(0, 10);
}

function hasTextContent(message) {
  return typeof message?.content === 'string'
    ? message.content.trim().length > 0
    : !!message?.content;
}

function isRedundantBulkSummaryMessage(mainMessages, index) {
  const message = mainMessages[index];
  if (!message || message.widgetType) return false;
  if (message.role !== 'assistant' && message.role !== 'system') return false;

  const content = String(message.content || '').trim();
  if (!/^bulk order update:\s*\d+\s*\/\s*\d+\s*applied\b/i.test(content)) {
    return false;
  }

  // Hide only when this text belongs to an adjacent bulk update outcome.
  for (let i = index - 1; i >= 0; i -= 1) {
    const previous = mainMessages[i];
    if (!previous) continue;
    if (previous.role === 'user') break;
    if (previous.widgetType === 'order_bulk_result' || previous.widgetType === 'order_bulk_update') {
      return true;
    }
    if (previous.widgetType === 'order_bulk_criteria') break;
  }
  return false;
}

function shouldHideMessage(mainMessages, index, suppressProgress, suppressOutcome) {
  const message = mainMessages[index];
  if (!message) return true;

  const hasWidget = !!message.widgetType;
  const hasText = hasTextContent(message);
  const hasAttachments = Array.isArray(message.attachments) && message.attachments.length > 0;

  if (
    message.isComplete === true
    && !hasText
    && !hasWidget
    && !hasAttachments
    && (message.role === 'assistant' || message.role === 'system')
  ) {
    return true;
  }
  if (message.widgetType === 'progress' && suppressProgress) {
    return true;
  }
  if (HIDDEN_CONFIRM_OUTCOME_WIDGET_TYPES.has(message.widgetType)) {
    return true;
  }
  if (
    suppressOutcome
    && SUPPRESSIBLE_CONFIRM_OUTCOME_WIDGET_TYPES.has(message.widgetType)
  ) {
    return true;
  }
  if (isRedundantBulkSummaryMessage(mainMessages, index)) {
    return true;
  }
  return false;
}

/**
 * MessageListV2 — virtualized, reverse-scroll message list.
 *
 * Uses react-virtuoso for efficient rendering of large message histories.
 * Shows date dividers, auto-scrolls on new messages when the reader is at
 * (or within 80px of) the bottom, and offers a Slack-style "Jump to latest"
 * pill when the reader has scrolled up to read history.
 */
function MessageListV2({
  messages,
  hasMore,
  loading,
  error,
  onRetry,
  onFetchMore,
  onConfirmationSubmitted,
  currentUserId,
  // KAP-313 — drives the Delete kebab item for admin viewers on messages
  // they did not author. Sender always sees Delete regardless.
  isAdminViewer,
  onOpenThread,
  onAddReaction,
  onToggleReaction,
  onEditMessage,
  onDeleteMessage,
  // KAP-311 — message-interaction callbacks + per-message bookmark state.
  // `bookmarkedMessageIds` is a Set<string> projected by the parent from
  // listBookmarks; lookup is O(1). `quickReactTokens` is the user-persisted
  // trio (or default fallback) threaded down to MessageHoverToolbar.
  onForwardMessage,
  onPinMessage,
  onUnpinMessage,
  onBookmarkMessage,
  onUnbookmarkMessage,
  bookmarkedMessageIds,
  quickReactTokens,
  // KAP-309 — when set, scroll the virtualizer to the matching message
  // (forces virtuoso to mount it), pulse the highlight, then fire
  // `onScrolledToMessageId(id)`. Parent uses the callback to strip the
  // `?msg=` URL param so the effect doesn't re-fire on re-render.
  scrollToMessageId,
  onScrolledToMessageId
}) {
  const virtuosoRef = useRef(null);
  const [atBottom, setAtBottom] = useState(true);
  const atBottomRef = useRef(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const prevCountRef = useRef(0);
  // Set by the `kapsa-own-message-sent` event when the user sends their own
  // message. Consumed (and cleared) by the next `followOutput` evaluation so
  // virtuoso scrolls to bottom regardless of the current atBottom state —
  // sending IS the user's intent to read the result. (KAP-312)
  const ownSendPendingRef = useRef(false);

  // Filter: only main-channel messages (no thread replies), sorted oldest-first
  // Drop completed empty non-widget rows before virtualization to prevent
  // zero-height anchor items from suppressing history rendering. Attachment-
  // only sends (image or PDF, no caption) explicitly count as non-empty so
  // the bubble keeps a visible footprint and the user sees their upload.
  const mainMessages = useMemo(
    () =>
      messages
        .filter((m) => {
          if (m.threadParentId) return false;
          // Soft-deleted messages are hidden entirely from the channel
          // feed — the row vanishes rather than showing a "This message
          // was deleted" placeholder. The row still exists in DynamoDB
          // (deletedAt + deletedBy + blanked content) for audit and
          // thread-parent context; we just don't render it inline.
          // Supersedes the earlier KAP-313 placeholder approach.
          if (m.deletedAt) return false;
          // KAP-311.1: a forwarded message with no caller note has empty
          // `content` — the quote block IS the payload. Keep the empty-
          // content guard from dropping it.
          if (m.forwardedFrom) return true;
          const hasWidget = !!m.widgetType;
          const hasTextContent = typeof m.content === 'string'
            ? m.content.trim().length > 0
            : !!m.content;
          const hasAttachments = Array.isArray(m.attachments) && m.attachments.length > 0;
          return !(m.isComplete === true && !hasWidget && !hasTextContent && !hasAttachments);
        })
        .sort((a, b) => {
          const timeA = toMillis(a.createdAt || a.timestamp);
          const timeB = toMillis(b.createdAt || b.timestamp);
          if (timeA !== timeB) return timeA - timeB;
          return String(a.id || '').localeCompare(String(b.id || ''));
        }),
    [messages]
  );

  const assistantFollowUpByMessageId = useMemo(
    () => buildAssistantFollowUpByMessageId(mainMessages),
    [mainMessages]
  );
  const bulkFollowUpByMessageId = useMemo(
    () => buildBulkFollowUpByMessageId(mainMessages),
    [mainMessages]
  );

  const suppressProgressAfterConfirmByMessageId = useMemo(
    () => buildSuppressProgressAfterConfirmByMessageId(mainMessages),
    [mainMessages]
  );
  const suppressOutcomeAfterConfirmByMessageId = useMemo(
    () => buildSuppressOutcomeAfterConfirmByMessageId(mainMessages),
    [mainMessages]
  );

  const visibleMainMessages = useMemo(
    () =>
      mainMessages.filter((message, index) => {
        const suppressProgress = !!suppressProgressAfterConfirmByMessageId.get(message.id);
        const suppressOutcome = !!suppressOutcomeAfterConfirmByMessageId.get(message.id);
        return !shouldHideMessage(mainMessages, index, suppressProgress, suppressOutcome);
      }),
    [mainMessages, suppressProgressAfterConfirmByMessageId, suppressOutcomeAfterConfirmByMessageId]
  );

  // Build items with date dividers interleaved
  const items = useMemo(() => {
    const result = [];
    let prevDate = null;

    for (let i = 0; i < visibleMainMessages.length; i++) {
      const msg = visibleMainMessages[i];
      const msgTimestamp = msg.createdAt || msg.timestamp;
      const msgDateKey = dateKey(msgTimestamp);

      // Date divider
      if (msgDateKey !== prevDate) {
        result.push({
          type: 'date-divider',
          date: toIsoTimestamp(msgTimestamp),
          key: `div-${msgDateKey}`
        });
        prevDate = msgDateKey;
      }

      result.push({ type: 'message', message: msg, key: msg.id });
    }

    return result;
  }, [visibleMainMessages]);

  // Mirror atBottom into a ref for synchronous reads inside non-React handlers
  // (image-loaded listener, virtuoso predicate). Also clear the unread counter
  // any time the user returns to the bottom.
  useEffect(() => {
    atBottomRef.current = atBottom;
    if (atBottom && unreadCount !== 0) {
      setUnreadCount(0);
    }
  }, [atBottom, unreadCount]);

  // Unread counter: when a new message arrives while the user is NOT at the
  // bottom, increment per arrival. Streaming AI responses grow an existing
  // message in place and do NOT change `visibleMainMessages.length`, so this
  // effect naturally ignores them — matching KAP-312 acceptance criterion 4.
  // The own-send override means the user's own message does not tick the
  // counter (followOutput will scroll to bottom, restoring atBottom shortly).
  useEffect(() => {
    const currentLength = visibleMainMessages.length;
    const delta = currentLength - prevCountRef.current;
    if (delta > 0 && !atBottomRef.current && !ownSendPendingRef.current) {
      setUnreadCount((c) => c + delta);
    }
    prevCountRef.current = currentLength;
  }, [visibleMainMessages.length]);

  // Listen for the "user sent a message" intent override dispatched by the
  // composer after a successful send. The next followOutput evaluation
  // consumes the flag and forces a scroll-to-bottom regardless of atBottom.
  // Same event-dispatch pattern as the attachment-image-loaded re-snap below.
  useEffect(() => {
    const handler = () => {
      ownSendPendingRef.current = true;
    };
    document.addEventListener('kapsa-own-message-sent', handler);
    return () => {
      document.removeEventListener('kapsa-own-message-sent', handler);
    };
  }, []);

  // When an attached image finishes loading, the message bubble grows. Re-snap
  // to the bottom so the full image is visible — but only if the user is
  // already pinned to the bottom (otherwise honor their scroll position).
  // The event is dispatched by MessageAttachments and bubbles up to us.
  useEffect(() => {
    const handler = () => {
      if (!atBottomRef.current || !virtuosoRef.current) return;
      // requestAnimationFrame: let the layout settle from the image's
      // intrinsic-size apply before measuring the new bottom.
      requestAnimationFrame(() => {
        virtuosoRef.current?.scrollToIndex({
          index: items.length - 1,
          behavior: 'auto',
          align: 'end'
        });
      });
    };
    document.addEventListener('kapsa-attachment-image-loaded', handler);
    return () => {
      document.removeEventListener('kapsa-attachment-image-loaded', handler);
    };
  }, [items.length]);

  // Load older messages when scrolling to top
  const handleStartReached = useCallback(() => {
    if (hasMore && !loading && onFetchMore) {
      onFetchMore();
    }
  }, [hasMore, loading, onFetchMore]);

  // Virtuoso evaluates this predicate synchronously each time totalCount grows,
  // BEFORE applying the new content — so the `isAtBottom` argument reflects
  // the pre-append state. This fixes the timing race in the prior
  // useEffect-based scroll, where virtuoso had already fired
  // atBottomStateChange(false) by the time React ran the effect. (KAP-312)
  const followOutput = useCallback((isAtBottom) => {
    if (ownSendPendingRef.current) {
      ownSendPendingRef.current = false;
      return 'smooth';
    }
    return isAtBottom ? 'smooth' : false;
  }, []);

  const handleJumpToLatest = useCallback(() => {
    if (!virtuosoRef.current || items.length === 0) return;
    virtuosoRef.current.scrollToIndex({
      index: items.length - 1,
      behavior: 'smooth',
      align: 'end'
    });
  }, [items.length]);

  // KAP-309 — `?msg=<id>` deeplink scroll. Triggered when the parent passes
  // a non-null `scrollToMessageId`. Virtuoso virtualises items, so a plain
  // `document.querySelector(...)` may miss a target that's in the model
  // but not mounted. We:
  //   1. Find the target's index in `items` (skipping date dividers).
  //   2. Call `virtuosoRef.current.scrollToIndex(...)` so virtuoso mounts
  //      the target's window.
  //   3. Poll the DOM briefly (every 80ms, up to 1.2s) for the
  //      `[data-message-id="..."]` node. Once it mounts, add the
  //      `data-msg-anchor` attribute to drive the CSS pulse animation;
  //      remove it after 1500ms so the pulse plays once.
  //   4. Notify the parent via `onScrolledToMessageId(id)` so it can strip
  //      the URL param. We fire the callback even if the polling times
  //      out — the parent's dedup will prevent re-tries against the same
  //      id, and leaving the param in the URL would cause the deeplink to
  //      keep re-triggering on every render.
  useEffect(() => {
    if (!scrollToMessageId) return undefined;
    const targetId = scrollToMessageId;
    const targetIndex = items.findIndex(
      (it) => it.type === 'message' && it.message && it.message.id === targetId
    );
    if (targetIndex < 0) {
      // Target isn't in the rendered item list at all (e.g. filtered out
      // by widget suppression). Tell the parent so it stops trying.
      if (typeof onScrolledToMessageId === 'function') onScrolledToMessageId(targetId);
      return undefined;
    }
    if (!virtuosoRef.current) return undefined;

    virtuosoRef.current.scrollToIndex({
      index: targetIndex,
      align: 'center',
      behavior: 'smooth'
    });

    let attempts = 0;
    const maxAttempts = 15; // ~1.2s at 80ms intervals
    let removeAttrTimer = 0;
    const sel = (typeof CSS !== 'undefined' && typeof CSS.escape === 'function')
      ? `[data-message-id="${CSS.escape(targetId)}"]`
      : `[data-message-id="${targetId}"]`;
    const intervalId = window.setInterval(() => {
      attempts += 1;
      const node = document.querySelector(sel);
      if (node) {
        node.setAttribute('data-msg-anchor', 'true');
        removeAttrTimer = window.setTimeout(() => {
          node.removeAttribute('data-msg-anchor');
        }, 1500);
        window.clearInterval(intervalId);
        if (typeof onScrolledToMessageId === 'function') onScrolledToMessageId(targetId);
      } else if (attempts >= maxAttempts) {
        window.clearInterval(intervalId);
        // Timed out trying to mount — clear the URL anyway so we don't
        // loop forever, but skip the pulse since we never found a node.
        if (typeof onScrolledToMessageId === 'function') onScrolledToMessageId(targetId);
      }
    }, 80);

    return () => {
      window.clearInterval(intervalId);
      if (removeAttrTimer) window.clearTimeout(removeAttrTimer);
    };
  }, [scrollToMessageId, items, onScrolledToMessageId]);

  const renderItem = useCallback(
    (index) => {
      const item = items[index];
      if (!item) return null;

      if (item.type === 'date-divider') {
        return (
          <div className="msg-date-divider" key={item.key}>
            <span className="msg-date-divider-line" />
            <span className="msg-date-divider-label">{formatDateDivider(item.date)}</span>
            <span className="msg-date-divider-line" />
          </div>
        );
      }

      const isBookmarked = bookmarkedMessageIds
        ? bookmarkedMessageIds.has(item.message.id)
        : false;

      return (
        <MessageBubble
          key={item.key}
          message={item.message}
          currentUserId={currentUserId}
          isAdmin={isAdminViewer}
          hasAssistantFollowUp={assistantFollowUpByMessageId.get(item.message.id)}
          bulkFollowUp={bulkFollowUpByMessageId.get(item.message.id)}
          suppressProgressAfterConfirm={suppressProgressAfterConfirmByMessageId.get(item.message.id)}
          suppressOutcomeAfterConfirm={suppressOutcomeAfterConfirmByMessageId.get(item.message.id)}
          onConfirmationSubmitted={onConfirmationSubmitted}
          onOpenThread={onOpenThread}
          onAddReaction={onAddReaction}
          onToggleReaction={onToggleReaction}
          onEditMessage={onEditMessage}
          onDeleteMessage={onDeleteMessage}
          onForwardMessage={onForwardMessage}
          onPinMessage={onPinMessage}
          onUnpinMessage={onUnpinMessage}
          onBookmarkMessage={onBookmarkMessage}
          onUnbookmarkMessage={onUnbookmarkMessage}
          isBookmarked={isBookmarked}
          quickReactTokens={quickReactTokens}
        />
      );
    },
    [
      items,
      currentUserId,
      isAdminViewer,
      assistantFollowUpByMessageId,
      bulkFollowUpByMessageId,
      suppressProgressAfterConfirmByMessageId,
      suppressOutcomeAfterConfirmByMessageId,
      onConfirmationSubmitted,
      onOpenThread,
      onAddReaction,
      onToggleReaction,
      onEditMessage,
      onDeleteMessage,
      onForwardMessage,
      onPinMessage,
      onUnpinMessage,
      onBookmarkMessage,
      onUnbookmarkMessage,
      bookmarkedMessageIds,
      quickReactTokens
    ]
  );

  if (visibleMainMessages.length === 0 && error && !loading) {
    return (
      <div className="msg-list-empty msg-list-error">
        <p>Couldn't load messages.</p>
        {onRetry ? (
          <button type="button" onClick={onRetry} className="msg-list-retry">
            Retry
          </button>
        ) : null}
      </div>
    );
  }

  if (visibleMainMessages.length === 0 && !loading) {
    return (
      <div className="msg-list-empty">
        <p>No messages yet. Start the conversation!</p>
      </div>
    );
  }

  return (
    <div className="msg-list-container">
      {loading && items.length === 0 && (
        <div className="msg-list-loading">Loading messages...</div>
      )}
      <Virtuoso
        ref={virtuosoRef}
        style={{ height: '100%' }}
        totalCount={items.length}
        itemContent={renderItem}
        initialTopMostItemIndex={items.length > 0 ? items.length - 1 : 0}
        followOutput={followOutput}
        atBottomStateChange={setAtBottom}
        atBottomThreshold={AT_BOTTOM_THRESHOLD_PX}
        startReached={handleStartReached}
        overscan={200}
        alignToBottom
        components={{
          Header: () =>
            loading && hasMore ? (
              <div className="msg-list-loading-more">Loading older messages...</div>
            ) : null
        }}
      />
      {!atBottom && unreadCount > 0 ? (
        <JumpToLatestPill count={unreadCount} onClick={handleJumpToLatest} />
      ) : null}
    </div>
  );
}

export default MessageListV2;
